import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Copy, Landmark, QrCode } from "lucide-react";
import { useLang } from "@/i18n";
import api, { errMsg } from "@/api";

const inputCls = "w-full px-4 py-3 rounded-xl border border-emerald-200 bg-white focus:outline-none focus:ring-2 focus:ring-emerald-700 text-sm";

export default function Donation() {
  const { t } = useLang();
  const [settings, setSettings] = useState(null);
  const [copied, setCopied] = useState("");
  const [loading, setLoading] = useState(false);
  const [form, setForm] = useState({ name: "", mobile: "", amount: "", message: "" });

  useEffect(() => {
    api.get("/settings/donation").then((r) => setSettings(r.data)).catch(() => {});
  }, []);

  const copy = async (text, key) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopied(key);
      setTimeout(() => setCopied(""), 2000);
    } catch {
      toast.error("Copy failed");
    }
  };

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await api.post("/donations", { ...form, message: form.message || null });
      toast.success(t.donation_success);
      setForm({ name: "", mobile: "", amount: "", message: "" });
    } catch (err) {
      toast.error(errMsg(err));
    } finally {
      setLoading(false);
    }
  };

  const DetailRow = ({ label, value, k }) => (
    <div className="flex justify-between items-center gap-4 border-b border-emerald-100 py-3">
      <span className="text-sm text-slate-500">{label}</span>
      <span className="flex items-center gap-2">
        <span className="font-semibold text-emerald-950 text-sm" data-testid={`bank-${k}`}>{value || "—"}</span>
        {value && (
          <button onClick={() => copy(value, k)} data-testid={`copy-${k}-btn`}
            className="p-1.5 rounded-lg hover:bg-emerald-100 transition-colors" title={t.copy}>
            <Copy className="w-4 h-4 text-emerald-700" />
          </button>
        )}
        {copied === k && <span className="text-xs text-amber-600 font-semibold">{t.copied}</span>}
      </span>
    </div>
  );

  return (
    <div data-testid="donation-page">
      <section className="bg-emerald-950 text-white py-14">
        <div className="max-w-7xl mx-auto px-4 sm:px-6">
          <h1 className="font-heading text-3xl sm:text-4xl lg:text-5xl font-extrabold" data-testid="donation-heading">{t.donation_title}</h1>
          <p className="text-amber-400 mt-2">{t.donation_subtitle}</p>
        </div>
      </section>

      <section className="max-w-7xl mx-auto px-4 sm:px-6 py-14">
        <div className="grid lg:grid-cols-2 gap-10">
          <div className="space-y-8">
            <div className="bg-white rounded-2xl border border-emerald-100 shadow-sm p-8">
              <div className="flex items-center gap-4 mb-6">
                <div className="w-12 h-12 rounded-2xl bg-emerald-800 flex items-center justify-center">
                  <QrCode className="w-6 h-6 text-white" />
                </div>
                <h2 className="font-heading text-xl font-bold text-emerald-950">{t.upi_id}</h2>
              </div>
              <div className="flex items-center justify-between gap-4 bg-emerald-50 rounded-xl px-5 py-4">
                <span className="font-mono font-bold text-emerald-950" data-testid="upi-id-value">
                  {settings?.upi_id || "—"}
                </span>
                {settings?.upi_id && (
                  <button onClick={() => copy(settings.upi_id, "upi")} data-testid="copy-upi-btn"
                    className="px-4 py-2 rounded-full bg-emerald-800 text-white text-xs font-semibold hover:bg-emerald-700 transition-colors">
                    {copied === "upi" ? t.copied : t.copy}
                  </button>
                )}
              </div>
            </div>

            <div className="bg-white rounded-2xl border border-emerald-100 shadow-sm p-8">
              <div className="flex items-center gap-4 mb-4">
                <div className="w-12 h-12 rounded-2xl bg-amber-600 flex items-center justify-center">
                  <Landmark className="w-6 h-6 text-white" />
                </div>
                <h2 className="font-heading text-xl font-bold text-emerald-950">{t.bank_details}</h2>
              </div>
              <DetailRow label={t.account_name} value={settings?.account_name} k="account_name" />
              <DetailRow label={t.account_number} value={settings?.account_number} k="account_number" />
              <DetailRow label={t.ifsc} value={settings?.ifsc} k="ifsc" />
              <DetailRow label={t.bank_name} value={settings?.bank_name} k="bank_name" />
              <DetailRow label={t.branch} value={settings?.branch} k="branch" />
            </div>
          </div>

          <div className="bg-white rounded-2xl border border-emerald-100 shadow-sm p-8 h-fit">
            <h2 className="font-heading text-xl font-bold text-emerald-950 mb-2">{t.donation_form_title}</h2>
            <p className="text-sm text-slate-500 mb-6">{t.donation_form_sub}</p>
            <form onSubmit={handleSubmit} className="space-y-5" data-testid="donation-form">
              <div>
                <label className="block text-xs font-semibold text-emerald-900 mb-1.5 uppercase tracking-wide">{t.form_name} *</label>
                <input required value={form.name} onChange={set("name")} className={inputCls} data-testid="donor-name-input" />
              </div>
              <div>
                <label className="block text-xs font-semibold text-emerald-900 mb-1.5 uppercase tracking-wide">{t.form_mobile} *</label>
                <input required value={form.mobile} onChange={set("mobile")} className={inputCls} data-testid="donor-mobile-input" />
              </div>
              <div>
                <label className="block text-xs font-semibold text-emerald-900 mb-1.5 uppercase tracking-wide">{t.amount} *</label>
                <input required value={form.amount} onChange={set("amount")} className={inputCls} data-testid="donor-amount-input" placeholder="e.g. 501" />
              </div>
              <div>
                <label className="block text-xs font-semibold text-emerald-900 mb-1.5 uppercase tracking-wide">{t.message}</label>
                <textarea value={form.message} onChange={set("message")} rows={3} className={inputCls} data-testid="donor-message-input" />
              </div>
              <button type="submit" disabled={loading} data-testid="donation-submit-btn"
                className="w-full py-3.5 rounded-full bg-amber-600 hover:bg-amber-700 text-white font-bold transition-all disabled:opacity-60">
                {loading ? "..." : t.submit}
              </button>
            </form>
          </div>
        </div>
      </section>
    </div>
  );
}
